/**
 * Meeting Calendar JavaScript
 *
 * Integrates FullCalendar for displaying meetings.
 *
 * @package
 * @since 2.0.0
 */

/* global FullCalendar, DemocraticCalendar */

(function () {
	'use strict';

	let calendar = null;
	const currentFilters = {
		committee_id: '',
		status: '',
	};

	/**
   * Initialize calendar
   */
	function initCalendar() {
		const calendarEl = document.getElementById('meetings-calendar');
		if (!calendarEl) return;

		calendar = new FullCalendar.Calendar(calendarEl, {
			initialView: 'dayGridMonth',
			headerToolbar: {
				left: 'prev,next',
				center: 'title',
				right: '',
			},
			height: 'auto',
			eventDisplay: 'block',
			events: fetchEvents,
			eventClick: handleEventClick,
			eventClassNames(arg) {
				const props = arg.event.extendedProps || {};
				if (props.type === 'bank_holiday') {
					return ['event-type-bank-holiday'];
				}
				return [`event-status-${props.status}`];
			},
			eventContent(arg) {
				const props = arg.event.extendedProps || {};
				if (props.type === 'bank_holiday') {
					return {
						html: `
						<div class="fc-event-main-frame fc-bank-holiday-event">
							<div class="fc-event-title">${arg.event.title}</div>
						</div>
					`,
					};
				}
				return {
					html: `
						<div class="fc-event-main-frame">
							<div class="fc-event-time">${arg.timeText}</div>
							<div class="fc-event-title">${arg.event.title}</div>
							${
	props.committee
		? `<div class="fc-event-committee">${props.committee}</div>`
		: ''
	}
						</div>
					`,
				};
			},
		});

		calendar.render();
	}

	/**
   * Fetch events from REST API
   *
   * @param {Object}   fetchInfo       - FullCalendar fetch info object
   * @param {Function} successCallback - Success callback function
   * @param {Function} failureCallback - Failure callback function
   */
	async function fetchEvents(fetchInfo, successCallback, failureCallback) {
		try {
			const params = new URLSearchParams({
				start: fetchInfo.startStr,
				end: fetchInfo.endStr,
				...currentFilters,
			});

			const response = await fetch(`${DemocraticCalendar.restUrl}?${params}`, {
				headers: {
					'X-WP-Nonce': DemocraticCalendar.nonce,
				},
			});

			if (!response.ok) {
				throw new Error('Failed to fetch events');
			}

			const responseData = await response.json();

			// The /calendar endpoint returns events directly in data.data array
			// Events are already in FullCalendar format (with deadlines included via filter)
			const events = responseData.data || responseData.meetings || [];

			successCallback(events);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Event fetch error:', error);
			failureCallback(error);
		}
	}

	/**
   * Get color for meeting status
   *
   * @param {string} _status - Meeting status
   * @return {string} Color code
   */
	function _getStatusColor(_status) {
		const colors = {
			scheduled: '#2196f3',
			in_progress: '#ff9800',
			completed: '#4caf50',
			cancelled: '#f44336',
		};
		return colors[_status] || '#2196f3';
	}

	/**
   * Handle event click
   *
   * @param {Object} info - FullCalendar event click info
   */
	function handleEventClick(info) {
		info.jsEvent.preventDefault();

		const meeting = info.event;
		const props = meeting.extendedProps || {};

		if (props.type === 'bank_holiday') {
			const description =
				props.description ||
				'UK bank holiday. Public offices and many services may be closed.';
			const html = `
			<div class="meeting-modal bank-holiday-modal">
				<h3 id="modal-title">${meeting.title}</h3>
				<div class="meeting-info">
					<p><strong>Date:</strong> ${formatFullDate(meeting.start)}</p>
					<p><strong>Bank holiday:</strong> ${meeting.title}</p>
					<p>${description}</p>
				</div>
			</div>
		`;
			showModal(html);
			return;
		}

		const isCommunity = props.type === 'community_event';
		const location = props.location || props.venue || '';
		const typeOrCommittee = isCommunity
			? '<p><strong>Type:</strong> Community event</p>'
			: `<p><strong>Committee:</strong> ${props.committee || 'N/A'}</p>
					<p><strong>Status:</strong> ${props.status || ''}</p>`;
		const documents = !isCommunity
			? `<div class="meeting-documents">
					${props.agenda_url ? `<a href="${props.agenda_url}" class="btn">View Agenda</a>` : ''}
					${props.minutes_url ? `<a href="${props.minutes_url}" class="btn">View Minutes</a>` : ''}
				</div>`
			: '';
		const html = `
			<div class="meeting-modal">
				<h3 id="modal-title">${meeting.title}</h3>
				<div class="meeting-info">
					<p><strong>Date:</strong> ${formatFullDate(meeting.start)}</p>
					${typeOrCommittee}
					${location ? `<p><strong>Location:</strong> ${location}</p>` : ''}
				</div>
				${documents}
				<a href="${meeting.url}" class="btn btn-primary">${isCommunity ? 'Event details' : 'Full Details'}</a>
			</div>
		`;

		showModal(html);
	}

	/**
   * Store the element that had focus before modal opened
   */
	let previouslyFocusedElement = null;

	/**
   * Close modal and restore focus
   */
	function closeModal() {
		const modal = document.getElementById('meeting-modal-overlay');
		if (modal) {
			modal.remove();
			// Restore focus to the element that opened the modal
			if (previouslyFocusedElement) {
				previouslyFocusedElement.focus();
				previouslyFocusedElement = null;
			}
		}
	}

	/**
   * Get all focusable elements within a container
   *
   * @param {HTMLElement} container - Container element
   * @return {Array} Array of focusable elements
   */
	function getFocusableElements(container) {
		const focusableSelectors = [
			'a[href]',
			'button:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		];
		return Array.from(
			container.querySelectorAll(focusableSelectors.join(', ')),
		);
	}

	/**
   * Show modal with content
   *
   * @param {string} content - HTML content to display
   */
	/**
   * Handle keyboard events for modal accessibility
   *
   * @param {KeyboardEvent} e - Keyboard event
   */
	function handleModalKeydown(e) {
		const modal = document.getElementById('meeting-modal-overlay');
		if (!modal) return;

		const modalContent = modal.querySelector('.modal-content');
		if (!modalContent) return;

		// Escape key closes modal
		if (e.key === 'Escape') {
			e.preventDefault();
			closeModal();
			return;
		}

		// Tab key - trap focus within modal
		if (e.key === 'Tab') {
			const focusableElements = getFocusableElements(modalContent);
			if (focusableElements.length === 0) return;

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];

			// Handle focus trapping
			if (e.shiftKey && document.activeElement === firstElement) {
				// Shift + Tab on first element: wrap to last
				e.preventDefault();
				lastElement.focus();
			} else if (!e.shiftKey && document.activeElement === lastElement) {
				// Tab on last element: wrap to first
				e.preventDefault();
				firstElement.focus();
			}
		}
	}

	function showModal(content) {
		// Store currently focused element to restore later
		previouslyFocusedElement = document.activeElement;

		let modal = document.getElementById('meeting-modal-overlay');
		const isNewModal = !modal;

		if (isNewModal) {
			modal = document.createElement('div');
			modal.id = 'meeting-modal-overlay';
			modal.className = 'modal-overlay';
			document.body.appendChild(modal);

			// Close on overlay click
			modal.addEventListener('click', function (e) {
				if (e.target === modal) {
					closeModal();
				}
			});

			// Add keyboard listener to modal (only once)
			modal.addEventListener('keydown', handleModalKeydown);
		}

		modal.innerHTML = `
			<div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="modal-title" tabindex="-1">
				<button class="modal-close" aria-label="Close modal">&times;</button>
				${content}
			</div>
		`;

		const modalContent = modal.querySelector('.modal-content');
		const closeBtn = modal.querySelector('.modal-close');

		// Close button click handler
		closeBtn.addEventListener('click', closeModal);

		// Also listen on document for Escape (ensures it works even if focus is lost)
		const escapeHandler = function (e) {
			if (e.key === 'Escape') {
				e.preventDefault();
				closeModal();
				document.removeEventListener('keydown', escapeHandler);
			}
		};
		document.addEventListener('keydown', escapeHandler);

		// Focus the modal content first, then the close button
		requestAnimationFrame(() => {
			modalContent.focus();
			requestAnimationFrame(() => {
				closeBtn.focus();
			});
		});
	}

	/**
   * Format date for display
   *
   * @param {Date} date - Date object
   * @return {string} Formatted date string
   */
	function formatFullDate(date) {
		return new Date(date).toLocaleDateString('en-GB', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	}

	/**
   * Initialize filters
   */
	function initFilters() {
		// Committee filter
		document
			.getElementById('filter-committee')
			?.addEventListener('change', function () {
				currentFilters.committee_id = this.value;
				calendar.refetchEvents();
			});

		// Status filter
		document
			.getElementById('filter-status')
			?.addEventListener('change', function () {
				currentFilters.status = this.value;
				calendar.refetchEvents();
			});

		// View toggle buttons
		document.querySelectorAll('.view-btn').forEach((btn) => {
			btn.addEventListener('click', function () {
				document
					.querySelectorAll('.view-btn')
					.forEach((b) => b.classList.remove('active'));
				this.classList.add('active');
			});
		});
	}

	/**
   * Initialize everything
   */
	function init() {
		initCalendar();
		initFilters();
		initCalendarKeyboardNav();
	}

	/**
   * Initialize keyboard navigation for calendar
   * Arrow keys navigate between periods (week/month/year based on current view)
   */
	function initCalendarKeyboardNav() {
		const calendarEl = document.getElementById('meetings-calendar');
		if (!calendarEl || !calendar) return;

		// Make the calendar container focusable
		calendarEl.setAttribute('tabindex', '0');
		calendarEl.setAttribute(
			'aria-label',
			'Meeting calendar. Use left and right arrow keys to navigate between periods.',
		);

		// Add keyboard event listener
		calendarEl.addEventListener('keydown', function (e) {
			// Only handle arrow keys when calendar is focused (not when inside an event/input)
			if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;

			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				calendar.prev();
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				calendar.next();
			} else if (e.key === 'Home') {
				// Go to today
				e.preventDefault();
				calendar.today();
			}
		});

		// Visual focus styles
		calendarEl.addEventListener('focus', function () {
			this.style.outline = '2px solid #3b82f6';
			this.style.outlineOffset = '2px';
		});

		calendarEl.addEventListener('blur', function () {
			this.style.outline = '';
			this.style.outlineOffset = '';
		});
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
